import { getGAClientId, getGASessionId } from 'utils/googleAnalitycsFunctions';
import { validateTel } from 'utils/forms/formValidations';

const FORM_CAPTURE_SCRIPT_URL =
  'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/can/FormCapture/FormCapture.bundle.js';

const SITECORE_LOG_FIELD_TO_DATAVERSE_FIELD = {
  firstName: 'firstname',
  lastName: 'lastname',
  email: 'emailaddress1',
  phoneNumber: 'mobilephone',
  postalZipCode: 'address1_postalcode',
  isAgentCheck: 'mam_web_isrealestateagent',
  urlForm: 'mam_mattamyhomesurl',
  divisionCode: 'mam_divisioncode',
  mpcCode: 'mam_mpccode',
  country: 'address1_country',
  promoCode: 'mam_promocode',
  creationMethod: 'mam_creationmethodcode',
  JDELot: 'mam_jdelotnumber',
  planName: 'mam_planname',
  qmiName: 'mam_qmiaddress',
  mhComments: 'mam_comments',
  consentCheck: 'mam_web_ismarketingexpressedconsent',
  gaClientId: 'mam_clientid',
  gaSessionId: 'mam_sessionid',
  smsChecked: 'trd_donotsms', // donotsms
  utmSource: 'mam_utmsource',
  utmMedium: 'mam_utmmedium',
  utmCampaign: 'mam_utmcampaign',
  utmTerm: 'mam_utmterm',
  utmContent: 'mam_utmcontent',
  dd1: 'mam_marketingcustomfield1',
  dd2: 'mam_marketingcustomfield2',
  dd3: 'mam_marketingcustomfield3',
  homeTypes: 'mam_hometype',
  priceRanges: 'mam_newbudget',
  newBudget: 'mam_newbudget',
  firstTimeHomeBuyer: 'mam_areyouafirsttimehomebuyer',
  bedroomCount: 'mam_bedroomcount',
  desiredMoveDate: 'mam_desiredmovedate',
};

const DYNAMX_FIELD_NAMES = {
  firstName: 'first-name',
  lastName: 'last-name',
  email: 'email-address',
  country: 'country',
  zipCode: 'zip-code',
  phone: 'telephone',
  comments: 'aditional-requirements',
  community: 'community-of-interest',
  isAgent: 'realestate',
  sms: 'sms-notifications',
  consent: 'terms',
  dd1: 'dd1',
  dd2: 'dd2',
  dd3: 'dd3',
  homeTypes: 'home-types',
  priceRanges: 'price-ranges',
  newBudget: 'new-budget',
  firstTimeHomeBuyer: 'first-time-home-buyer',
  bedroomCount: 'bedroom-count',
  desiredMoveDate: 'desired-move-date',
};

const parsePayloadData = (payloadData) => {
  if (!payloadData || typeof payloadData !== 'string') return payloadData;

  try {
    return JSON.parse(payloadData);
  } catch (e) {
    if (!payloadData.includes('=')) return payloadData;

    const params = new URLSearchParams(payloadData);
    if (!Array.from(params).length) return payloadData;

    return Array.from(params).reduce((values, [key, value]) => {
      values[key] = value;
      return values;
    }, {});
  }
};

const collectPayloadValues = (payloadData, values = {}) => {
  if (!payloadData) return values;

  if (Array.isArray(payloadData)) {
    payloadData.forEach((item) => collectPayloadValues(item, values));
    return values;
  }

  if (typeof payloadData !== 'object') return values;

  const fieldName =
    payloadData.DataverseFieldName ||
    payloadData.dataverseFieldName ||
    payloadData.name ||
    payloadData.Name ||
    payloadData.key ||
    payloadData.Key;
  const fieldValue =
    payloadData.DataverseFieldValue ??
    payloadData.dataverseFieldValue ??
    payloadData.value ??
    payloadData.Value ??
    payloadData.fieldValue ??
    payloadData.FieldValue;

  if (fieldName && fieldValue !== undefined) {
    values[fieldName] = fieldValue;
  }

  Object.entries(payloadData).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (typeof value === 'object') {
      collectPayloadValues(value, values);
      return;
    }

    values[key] = value;
  });

  return values;
};

const getPayloadValues = (payload) => {
  const parsedPayloadData = parsePayloadData(payload && payload.data);
  const values = collectPayloadValues(payload);
  return collectPayloadValues(parsedPayloadData, values);
};

const normalizeComparedValue = (value) =>
  value === null || value === undefined ? '' : String(value);

export const getPayloadVerifiedSitecoreFormData = (
  sitecoreFormData,
  payload,
) => {
  const payloadValues = getPayloadValues(payload);
  const verifiedSitecoreFormData = { ...sitecoreFormData };

  Object.entries(SITECORE_LOG_FIELD_TO_DATAVERSE_FIELD).forEach(
    ([sitecoreFieldName, dataverseFieldName]) => {
      if (
        !Object.prototype.hasOwnProperty.call(
          verifiedSitecoreFormData,
          sitecoreFieldName,
        ) ||
        !Object.prototype.hasOwnProperty.call(payloadValues, dataverseFieldName)
      ) {
        return;
      }

      const payloadValue = payloadValues[dataverseFieldName];
      const sitecoreValue = verifiedSitecoreFormData[sitecoreFieldName];

      if (
        normalizeComparedValue(sitecoreValue) !==
        normalizeComparedValue(payloadValue)
      ) {
        verifiedSitecoreFormData[sitecoreFieldName] = payloadValue;
      }
    },
  );

  return verifiedSitecoreFormData;
};

const removeExistingInput = (sitecoreForm, name) => {
  const existingInput = sitecoreForm.querySelectorAll(
    `input[name='${name}']`,
  )[0];
  if (existingInput) existingInput.remove();
};

const appendHiddenInput = (sitecoreForm, name, value, shouldAppend = true) => {
  removeExistingInput(sitecoreForm, name);

  const input = document.createElement('input');
  input.setAttribute('type', 'text');
  input.setAttribute('name', name);
  input.setAttribute('class', 'hidden');
  input.setAttribute('value', value ?? '');

  if (shouldAppend) {
    sitecoreForm.appendChild(input);
  }

  return input;
};

const getDropdownDisplayValue = (dropdown) =>
  dropdown
    ? dropdown.title + ': ' + dropdown.options[dropdown.selectedIndex].text
    : '';

const getRegisteredInput = (fieldRegistry, sitecoreFormModelName) => {
  const field = fieldRegistry?.getDynaMXField?.(sitecoreFormModelName);
  return field && field.inputElement ? field.inputElement : null;
};

const getFormInputBySitecoreModelName = (
  formElement,
  sitecoreFormModelName,
) => {
  if (!formElement || !sitecoreFormModelName) return null;

  const fields = formElement.querySelectorAll('input, select, textarea');
  return (
    Array.from(fields).find((field) => field.name === sitecoreFormModelName) ||
    null
  );
};

const getInputValue = (input) => {
  if (!input) return undefined;
  if (input.type === 'checkbox') return input.checked;
  return input.value;
};
const isRequiredInput = (input) =>
  !!input &&
  (input.required === true || input.getAttribute?.('aria-required') === 'true');

const resolveInput = ({ fieldRegistry, sitecoreFormModelName, fallback }) => {
  const input =
    getRegisteredInput(fieldRegistry, sitecoreFormModelName) ||
    (fallback ? fallback() : null);

  if (isRequiredInput(input) && !getInputValue(input)) {
    console.log('[Dynamics Forms Log] ' + sitecoreFormModelName + ' required');
  }

  return input;
};

// Fallback for fields whose input is named after the Sitecore model name.
const getOptionalFieldInput = ({
  formElement,
  fieldRegistry,
  sitecoreFormModelName,
}) =>
  resolveInput({
    fieldRegistry,
    sitecoreFormModelName,
    fallback: () =>
      getFormInputBySitecoreModelName(formElement, sitecoreFormModelName),
  });

const compactMappings = (mappings) =>
  mappings.filter((mapping) => mapping.FormFieldName);

const getDoNotSmsValue = (consentCheckBoxInput, phoneInput) => {
  const isConsentChecked = !!consentCheckBoxInput?.checked;
  const hasValidPhone = !!validateTel(phoneInput?.value);

  return isConsentChecked !== hasValidPhone;
};

const getExtraMappingValue = ({
  extraMapping,
  dynamicsFormFields,
  fieldRegistry,
}) => {
  if (extraMapping.fixedValue !== undefined) return extraMapping.fixedValue;

  const sourceInput = getRegisteredInput(
    fieldRegistry,
    extraMapping.sourceField,
  );
  const sourceValue = getInputValue(sourceInput);
  if (sourceValue !== undefined) return sourceValue;

  if (extraMapping.dynamicsFormField) {
    return dynamicsFormFields[extraMapping.dynamicsFormField];
  }

  return undefined;
};

const getDefaultHiddenInputName = (fieldName) =>
  `${String(fieldName || 'extra')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}Input`;

const buildExtraMappings = ({
  sitecoreForm,
  extraMappings,
  dynamicsFormFields,
  fieldRegistry,
  addNewLog,
}) =>
  extraMappings.reduce((mappings, extraMapping) => {
    /*
      Extra mapping shape:
      - sourceField: Sitecore form model name to read from the registry.
      - dataverseField: Dynamics/Dataverse target field name.
      - fixedValue: optional hardcoded value, highest priority.
      - dynamicsFormField: optional key to read from dynamicsFormFields.
      - hiddenInputName: optional hidden input name used for serializeForm.
      - transform: optional function that receives the resolved value.
      - valueMap: optional DataverseFieldValue conversion array.
      - required: logs a warning when the value cannot be resolved.
    */
    const value = getExtraMappingValue({
      extraMapping,
      dynamicsFormFields,
      fieldRegistry,
    });
    const transformedValue = extraMapping.transform
      ? extraMapping.transform(value)
      : value;

    if (
      transformedValue === undefined ||
      transformedValue === null ||
      transformedValue === ''
    ) {
      if (extraMapping.required && addNewLog) {
        addNewLog(
          300,
          '[Dynamics Forms API] Missing required extra mapping value for Dataverse field: ' +
            extraMapping.dataverseField,
        );
      }
      return mappings;
    }

    const sourceInput = getRegisteredInput(
      fieldRegistry,
      extraMapping.sourceField,
    );
    const shouldUseSourceInput =
      sourceInput &&
      !extraMapping.hiddenInputName &&
      extraMapping.fixedValue === undefined &&
      !extraMapping.dynamicsFormField &&
      !extraMapping.transform;
    const formFieldName = shouldUseSourceInput
      ? sourceInput.name
      : appendHiddenInput(
          sitecoreForm,
          extraMapping.hiddenInputName ||
            getDefaultHiddenInputName(extraMapping.dataverseField),
          transformedValue,
        ).name;

    mappings.push({
      FormFieldName: formFieldName,
      DataverseFieldName: extraMapping.dataverseField,
      ...(extraMapping.valueMap
        ? { DataverseFieldValue: extraMapping.valueMap }
        : {}),
    });

    return mappings;
  }, []);

export const secureDynaMXCaptureScript = (addNewLog) => {
  if (typeof window === 'undefined') return false;

  if (window.d365mktformcapture) {
    addNewLog(100, '[Dynamics Forms API] The Dynamics Form API is enabled');
    return true;
  }

  addNewLog(
    300,
    '[Dynamics Forms API] Something went wrong when trying to load the The Dynamics Form API script',
  );
  addNewLog(
    100,
    '[Dynamics Forms API] Trying to reload the scripts on form submit',
  );

  const formCaptureScript = document.createElement('script');
  formCaptureScript.src = FORM_CAPTURE_SCRIPT_URL;
  formCaptureScript.onload = () => {
    addNewLog(100, '[Dynamics Forms API] Dynamics Forms API script loaded');
  };
  formCaptureScript.onerror = () => {
    addNewLog(
      300,
      '[Dynamics Forms API] Something went wrong when trying to load the The Dynamics Form API script',
    );
  };
  document.head.appendChild(formCaptureScript);

  return false;
};

export const getSitecoreFormData = (
  formElement,
  dynamicsFormFields,
  hasCommunityDropdown = false,
  marketCommunitiesForm = [],
  isGenericForm = false,
  utmParams = {},
  fieldRegistry,
) => {
  const sitecoreFormData = {};
  if (typeof window === 'undefined' || !formElement) return sitecoreFormData;

  const firstNameInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.firstName,
    fallback: () => formElement.querySelectorAll('input[type=text]')[3],
  });
  sitecoreFormData.firstName = firstNameInput && firstNameInput.value;

  const lastNameInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.lastName,
    fallback: () => formElement.querySelectorAll('input[type=text]')[4],
  });
  sitecoreFormData.lastName = lastNameInput && lastNameInput.value;

  const emailInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.email,
    fallback: () => formElement.querySelector('input[type=email]'),
  });
  sitecoreFormData.email = emailInput && emailInput.value;

  const countryInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.country,
    fallback: () =>
      hasCommunityDropdown
        ? formElement.querySelectorAll('select')[1]
        : formElement.querySelectorAll('select')[0],
  });
  sitecoreFormData.country = countryInput && countryInput.value;

  const zipCodeInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.zipCode,
    fallback: () => formElement.querySelectorAll('input[type=text]')[5],
  });
  sitecoreFormData.postalZipCode = zipCodeInput && zipCodeInput.value;

  const mobilePhoneInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.phone,
    fallback: () => formElement.querySelectorAll('input[type=tel]')[0],
  });
  sitecoreFormData.phoneNumber = mobilePhoneInput && mobilePhoneInput.value;

  const commentsTextInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.comments,
    fallback: () => formElement.querySelectorAll('textarea')[0],
  });
  sitecoreFormData.mhComments = commentsTextInput && commentsTextInput.value;

  const allCheckBoxesArray = Array.from(
    formElement.querySelectorAll('input[type=checkbox]'),
  );
  const isAgentCheckBoxInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.isAgent,
    fallback: () => allCheckBoxesArray[0],
  });
  sitecoreFormData.isAgentCheck =
    isAgentCheckBoxInput && isAgentCheckBoxInput.checked;

  const messageTextCheck = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.sms,
    fallback: () => allCheckBoxesArray[1],
  });
  // Consent and SMS are authored as a single combined checkbox. Forms that have not
  // been migrated yet still render the legacy `terms` checkbox, so prefer that when it
  // is present and fall back to the combined checkbox when it is not.
  const consentCheckBoxInput =
    getOptionalFieldInput({
      formElement,
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.consent,
    }) ||
    messageTextCheck ||
    allCheckBoxesArray[2];
  sitecoreFormData.consentCheck =
    consentCheckBoxInput && consentCheckBoxInput.checked;
  sitecoreFormData.smsChecked = getDoNotSmsValue(
    consentCheckBoxInput,
    mobilePhoneInput,
  );

  sitecoreFormData.promoCode =
    dynamicsFormFields && dynamicsFormFields.promoCode
      ? dynamicsFormFields.promoCode
      : '';

  sitecoreFormData.mpcCode = dynamicsFormFields && dynamicsFormFields.mpc;
  sitecoreFormData.divisionCode =
    dynamicsFormFields && dynamicsFormFields.divisionCode;

  if (hasCommunityDropdown) {
    const communityInput = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.community,
      fallback: () => formElement.querySelectorAll('select')[0],
    });
    const selectedCommunity = marketCommunitiesForm.find(
      (e) => e.communityID === (communityInput && communityInput.value),
    );
    sitecoreFormData.mpcCode = selectedCommunity && selectedCommunity.mpcCode;
    sitecoreFormData.JDECommunity =
      selectedCommunity && selectedCommunity.displayName;
    sitecoreFormData.divisionCode =
      selectedCommunity && selectedCommunity.divisionCode;
  }

  sitecoreFormData.JDELot =
    dynamicsFormFields && dynamicsFormFields.qmiLotNumber
      ? dynamicsFormFields.qmiLotNumber
      : '';
  sitecoreFormData.planName =
    dynamicsFormFields && dynamicsFormFields.planName
      ? dynamicsFormFields.planName
      : '';
  sitecoreFormData.qmiName =
    dynamicsFormFields && dynamicsFormFields.qmiName
      ? dynamicsFormFields.qmiName
      : '';
  sitecoreFormData.urlForm = window.location.href;
  sitecoreFormData.gaClientId = getGAClientId() || '';
  sitecoreFormData.gaSessionId = getGASessionId() || '';

  if (utmParams.utmSource) sitecoreFormData.utmSource = utmParams.utmSource;
  if (utmParams.utmMedium) sitecoreFormData.utmMedium = utmParams.utmMedium;
  if (utmParams.utmCampaign)
    sitecoreFormData.utmCampaign = utmParams.utmCampaign;
  if (utmParams.utmTerm) sitecoreFormData.utmTerm = utmParams.utmTerm;
  if (utmParams.utmContent) sitecoreFormData.utmContent = utmParams.utmContent;

  if (isGenericForm) {
    const dd1Input = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd1,
    });
    sitecoreFormData.dd1 = dd1Input ? getDropdownDisplayValue(dd1Input) : '';

    const dd2Input = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd2,
    });
    sitecoreFormData.dd2 = dd2Input ? getDropdownDisplayValue(dd2Input) : '';

    const dd3Input = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd3,
    });
    sitecoreFormData.dd3 = dd3Input ? getDropdownDisplayValue(dd3Input) : '';

    const priceRanges = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.priceRanges,
    });
    sitecoreFormData.priceRanges = priceRanges ? priceRanges.value : '';

    const homeTypes = resolveInput({
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.homeTypes,
    });
    sitecoreFormData.homeTypes = homeTypes ? homeTypes.value : '';
  }

  const priceRangesInput = getOptionalFieldInput({
    formElement,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.priceRanges,
  });
  const newBudgetInput = getOptionalFieldInput({
    formElement,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.newBudget,
  });
  const firstTimeHomeBuyerInput = getOptionalFieldInput({
    formElement,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.firstTimeHomeBuyer,
  });
  const bedroomCountInput = getOptionalFieldInput({
    formElement,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.bedroomCount,
  });
  const desiredMoveDateInput = getOptionalFieldInput({
    formElement,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.desiredMoveDate,
  });

  if (priceRangesInput) {
    sitecoreFormData.priceRanges = priceRangesInput.value;
  } else if (newBudgetInput) {
    sitecoreFormData.newBudget = newBudgetInput.value;
  }
  if (firstTimeHomeBuyerInput) {
    sitecoreFormData.firstTimeHomeBuyer = firstTimeHomeBuyerInput.value;
  }
  if (bedroomCountInput) {
    sitecoreFormData.bedroomCount = bedroomCountInput.value;
  }
  if (desiredMoveDateInput) {
    sitecoreFormData.desiredMoveDate = desiredMoveDateInput.value;
  }

  return sitecoreFormData;
};

export const getMappings = ({
  sitecoreForm,
  hasCommunityDropdown,
  dynamicsFormFields = {},
  marketCommunitiesForm = [],
  isGenericForm = false,
  utmParams = {},
  variant = 'form',
  fieldRegistry,
  extraMappings = [],
  addNewLog,
}) => {
  const firstNameInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.firstName,
    fallback: () => sitecoreForm.querySelectorAll('input[type=text]')[3],
  });
  const lastNameInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.lastName,
    fallback: () => sitecoreForm.querySelectorAll('input[type=text]')[4],
  });
  const emailInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.email,
    fallback: () => sitecoreForm.querySelector('input[type=email]'),
  });
  const countryInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.country,
    fallback: () =>
      hasCommunityDropdown
        ? sitecoreForm.querySelectorAll('select')[1]
        : sitecoreForm.querySelectorAll('select')[0],
  });
  const zipCodeInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.zipCode,
    fallback: () => sitecoreForm.querySelectorAll('input[type=text]')[5],
  });
  const mobilePhoneSitecoreInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.phone,
    fallback: () => sitecoreForm.querySelectorAll('input[type=tel]')[0],
  });
  const commentsTextInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.comments,
    fallback: () => sitecoreForm.querySelectorAll('textarea')[0],
  });
  const allCheckBoxesArray = Array.from(
    sitecoreForm.querySelectorAll('input[type=checkbox]'),
  );
  const isAgentCheckBoxInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.isAgent,
    fallback: () => allCheckBoxesArray[0],
  });
  const isTextMessageCheckBoxInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.sms,
    fallback: () => allCheckBoxesArray[1],
  });
  // Consent and SMS are authored as a single combined checkbox. Forms that have not
  // been migrated yet still render the legacy `terms` checkbox, so prefer that when it
  // is present and fall back to the combined checkbox when it is not.
  const consentCheckBoxInput =
    getOptionalFieldInput({
      formElement: sitecoreForm,
      fieldRegistry,
      sitecoreFormModelName: DYNAMX_FIELD_NAMES.consent,
    }) ||
    isTextMessageCheckBoxInput ||
    allCheckBoxesArray[2];

  const communityInput = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.community,
    fallback: () => sitecoreForm.querySelectorAll('select')[0],
  });
  const selectedCommunity = hasCommunityDropdown
    ? marketCommunitiesForm.find(
        (e) => e.communityID === (communityInput && communityInput.value),
      )
    : null;
  const mpcInput = appendHiddenInput(
    sitecoreForm,
    'mpcInput',
    (variant === 'form' || variant === 'modal') && hasCommunityDropdown
      ? selectedCommunity && selectedCommunity.mpcCode
      : dynamicsFormFields.mpc,
  );

  const divisionAreaCodeInput = appendHiddenInput(
    sitecoreForm,
    'divisionAreaCodeInput',
    (variant === 'form' || variant === 'modal') && hasCommunityDropdown
      ? selectedCommunity && selectedCommunity.divisionCode
      : dynamicsFormFields.divisionCode,
  );
  const siteUrlInput = appendHiddenInput(
    sitecoreForm,
    'siteUrlInput',
    window.location.href || '',
  );
  const promoCodeInput = appendHiddenInput(
    sitecoreForm,
    'promoCodeInput',
    dynamicsFormFields.promoCode,
  );
  const jdeLotNumberInput = appendHiddenInput(
    sitecoreForm,
    'jdeLotNumberInput',
    dynamicsFormFields.qmiLotNumber,
  );
  const planNameInput = appendHiddenInput(
    sitecoreForm,
    'planNameInput',
    dynamicsFormFields.planName,
  );
  const qmiAddressInput = appendHiddenInput(
    sitecoreForm,
    'qmiAddressInput',
    dynamicsFormFields.qmiName,
  );
  const leadsourcetypeInput = appendHiddenInput(
    sitecoreForm,
    'leadsourcetypeInput',
    '860890003',
  );
  const mobilePhoneInput = appendHiddenInput(
    sitecoreForm,
    'mobilePhoneInput',
    mobilePhoneSitecoreInput
      ? mobilePhoneSitecoreInput.value.replace(/\D/g, '')
      : '',
  );
  const isRealEstateAgentInput = appendHiddenInput(
    sitecoreForm,
    'isRealEstateAgentInput',
    isAgentCheckBoxInput ? isAgentCheckBoxInput.checked : false,
  );
  const expressedConsentInput = appendHiddenInput(
    sitecoreForm,
    'expressedConsentInput',
    consentCheckBoxInput ? consentCheckBoxInput.checked : false,
  );
  const textMessageInput = appendHiddenInput(
    sitecoreForm,
    'textMessageInput',
    getDoNotSmsValue(consentCheckBoxInput, mobilePhoneSitecoreInput),
  );
  const gaClientIdInput = appendHiddenInput(
    sitecoreForm,
    'gaClientIdInput',
    getGAClientId() || '',
  );
  const gaSessionIdInput = appendHiddenInput(
    sitecoreForm,
    'gaSessionIdInput',
    getGASessionId() || '',
  );

  const dd1Value = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd1,
  });
  const price1DropdownHidden = appendHiddenInput(
    sitecoreForm,
    'dd1',
    dd1Value ? getDropdownDisplayValue(dd1Value) : '',
    variant === 'form',
  );
  const dd2Value = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd2,
  });
  const price2DropdownHidden = appendHiddenInput(
    sitecoreForm,
    'dd2',
    dd2Value ? getDropdownDisplayValue(dd2Value) : '',
    variant === 'form',
  );
  const dd3Value = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.dd3,
  });
  const price3DropdownHidden = appendHiddenInput(
    sitecoreForm,
    'dd3',
    dd3Value ? getDropdownDisplayValue(dd3Value) : '',
    variant === 'form',
  );
  const priceRangesValue = getOptionalFieldInput({
    formElement: sitecoreForm,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.priceRanges,
  });
  const priceRangesDropdownHidden = appendHiddenInput(
    sitecoreForm,
    'priceRanges',
    priceRangesValue ? priceRangesValue.value : '',
    variant === 'form',
  );
  const newBudgetValue = getOptionalFieldInput({
    formElement: sitecoreForm,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.newBudget,
  });
  const homeTypesValue = resolveInput({
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.homeTypes,
  });
  const homeTypesDropdownHidden = appendHiddenInput(
    sitecoreForm,
    'homeTypes',
    homeTypesValue ? homeTypesValue.value : '',
    variant === 'form',
  );
  const firstTimeHomeBuyerValue = getOptionalFieldInput({
    formElement: sitecoreForm,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.firstTimeHomeBuyer,
  });
  const bedroomCountValue = getOptionalFieldInput({
    formElement: sitecoreForm,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.bedroomCount,
  });
  const desiredMoveDateValue = getOptionalFieldInput({
    formElement: sitecoreForm,
    fieldRegistry,
    sitecoreFormModelName: DYNAMX_FIELD_NAMES.desiredMoveDate,
  });

  const utmSourceInput = appendHiddenInput(
    sitecoreForm,
    'utmSourceInput',
    utmParams.utmSource,
    Boolean(utmParams.utmSource),
  );
  const utmMediumInput = appendHiddenInput(
    sitecoreForm,
    'utmMediumInput',
    utmParams.utmMedium,
    Boolean(utmParams.utmMedium),
  );
  const utmCampaignInput = appendHiddenInput(
    sitecoreForm,
    'utmCampaignInput',
    utmParams.utmCampaign,
    Boolean(utmParams.utmCampaign),
  );
  const utmTermInput = appendHiddenInput(
    sitecoreForm,
    'utmTermInput',
    utmParams.utmTerm,
    Boolean(utmParams.utmTerm),
  );
  const utmContentInput = appendHiddenInput(
    sitecoreForm,
    'utmContentInput',
    utmParams.utmContent,
    Boolean(utmParams.utmContent),
  );
  const creationMethodInput = appendHiddenInput(
    sitecoreForm,
    'creationMethodInput',
    '860890005',
  );

  const sitecoreFormFields = {
    firstName: firstNameInput && firstNameInput.name,
    lastName: lastNameInput && lastNameInput.name,
    email: emailInput && emailInput.name,
    countryOfResidence: countryInput && countryInput.name,
    zipcode: zipCodeInput && zipCodeInput.name,
    phone: mobilePhoneInput.name,
    isRealEstateAgent: isRealEstateAgentInput.name,
    comments: commentsTextInput && commentsTextInput.name,
    mpcCode: mpcInput && mpcInput.name,
    divisionAreaCode: divisionAreaCodeInput.name,
    siteUrl: siteUrlInput.name,
    promocode: promoCodeInput.name,
    qmiaddress: qmiAddressInput.name,
    jdeLotNumber: jdeLotNumberInput.name,
    planName: planNameInput.name,
    leadsourcetype: leadsourcetypeInput.name,
    consent: expressedConsentInput.name,
    textMessage: textMessageInput.name,
    jdeCommunityNumber: '',
    elevationName: '',
    creationMethod: creationMethodInput.name,
    gaClientId: gaClientIdInput.name,
    gaSessionId: gaSessionIdInput.name,
    priceRanges: priceRangesDropdownHidden.name,
    newBudget: !priceRangesValue && newBudgetValue && newBudgetValue.name,
    firstTimeHomeBuyer: firstTimeHomeBuyerValue && firstTimeHomeBuyerValue.name,
    bedroomCount: bedroomCountValue && bedroomCountValue.name,
    desiredMoveDate: desiredMoveDateValue && desiredMoveDateValue.name,
  };

  if (utmParams.utmSource) sitecoreFormFields.utmSource = utmSourceInput.name;
  if (utmParams.utmMedium) sitecoreFormFields.utmMedium = utmMediumInput.name;
  if (utmParams.utmCampaign)
    sitecoreFormFields.utmCampaign = utmCampaignInput.name;
  if (utmParams.utmTerm) sitecoreFormFields.utmTerm = utmTermInput.name;
  if (utmParams.utmContent)
    sitecoreFormFields.utmContent = utmContentInput.name;

  if (isGenericForm) {
    sitecoreFormFields.dd1 = price1DropdownHidden.name;
    sitecoreFormFields.dd2 = price2DropdownHidden.name;
    sitecoreFormFields.dd3 = price3DropdownHidden.name;
    sitecoreFormFields.homeTypes = homeTypesDropdownHidden.name;
  }

  const mappings = [
    {
      FormFieldName: sitecoreFormFields.firstName,
      DataverseFieldName: 'firstname',
    },
    {
      FormFieldName: sitecoreFormFields.lastName,
      DataverseFieldName: 'lastname',
    },
    {
      FormFieldName: sitecoreFormFields.email,
      DataverseFieldName: 'emailaddress1',
    },
    {
      FormFieldName: sitecoreFormFields.phone,
      DataverseFieldName: 'mobilephone',
    },
    {
      FormFieldName: sitecoreFormFields.zipcode,
      DataverseFieldName: 'address1_postalcode',
    },
    {
      FormFieldName: sitecoreFormFields.isRealEstateAgent,
      DataverseFieldName: 'mam_web_isrealestateagent',
      DataverseFieldValue: [
        { FormValue: 'true', DataverseValue: '1' },
        { FormValue: 'false', DataverseValue: '0' },
      ],
    },
    {
      FormFieldName: sitecoreFormFields.leadsourcetype,
      DataverseFieldName: 'msdyncrm_leadsourcetype',
    },
    {
      FormFieldName: sitecoreFormFields.siteUrl,
      DataverseFieldName: 'mam_mattamyhomesurl',
    },
    {
      FormFieldName: sitecoreFormFields.divisionAreaCode,
      DataverseFieldName: 'mam_divisioncode',
    },
    {
      FormFieldName: sitecoreFormFields.mpcCode,
      DataverseFieldName: 'mam_mpccode',
    },
    {
      FormFieldName: sitecoreFormFields.countryOfResidence,
      DataverseFieldName: 'mam_marketingcountrycode',
      DataverseFieldValue: [
        { FormValue: 'CA', DataverseValue: '860890000' },
        { FormValue: 'US', DataverseValue: '860890001' },
        { FormValue: 'Other', DataverseValue: '860890002' },
      ],
    },
    {
      FormFieldName: sitecoreFormFields.countryOfResidence,
      DataverseFieldName: 'address1_country',
      DataverseFieldValue: [
        { FormValue: 'CA', DataverseValue: 'Canada' },
        { FormValue: 'US', DataverseValue: 'United States' },
      ],
    },
    {
      FormFieldName: sitecoreFormFields.promocode,
      DataverseFieldName: 'mam_promocode',
    },
    {
      FormFieldName: sitecoreFormFields.creationMethod,
      DataverseFieldName: 'mam_creationmethodcode',
    },
    {
      FormFieldName: sitecoreFormFields.qmiaddress,
      DataverseFieldName: 'mam_qmiaddress',
    },
    {
      FormFieldName: sitecoreFormFields.jdeCommunityNumber,
      DataverseFieldName: 'mam_jdecommunitynumber',
    },
    {
      FormFieldName: sitecoreFormFields.jdeLotNumber,
      DataverseFieldName: 'mam_jdelotnumber',
    },
    {
      FormFieldName: sitecoreFormFields.planName,
      DataverseFieldName: 'mam_planname',
    },
    {
      FormFieldName: sitecoreFormFields.elevationName,
      DataverseFieldName: 'mam_elevationname',
    },
    {
      FormFieldName: sitecoreFormFields.comments,
      DataverseFieldName: 'mam_comments',
    },
    {
      FormFieldName: sitecoreFormFields.consent,
      DataverseFieldName: 'mam_web_ismarketingexpressedconsent',
      DataverseFieldValue: [
        { FormValue: 'true', DataverseValue: '1' },
        { FormValue: 'false', DataverseValue: '0' },
      ],
    },
    {
      FormFieldName: sitecoreFormFields.gaClientId,
      DataverseFieldName: 'mam_clientid',
    },
    {
      FormFieldName: sitecoreFormFields.gaSessionId,
      DataverseFieldName: 'mam_sessionid',
    },
    {
      FormFieldName: sitecoreFormFields.textMessage,
      DataverseFieldName: 'trd_donotsms', // true 0 false 1 => getDoNotSmsValue()
      DataverseFieldValue: [
        { FormValue: 'true', DataverseValue: '1' },
        { FormValue: 'false', DataverseValue: '0' },
      ],
    },
  ];

  if (utmParams.utmSource) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmSource,
      DataverseFieldName: 'mam_utmsource',
    });
  }
  if (utmParams.utmMedium) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmMedium,
      DataverseFieldName: 'mam_utmmedium',
    });
  }
  if (utmParams.utmCampaign) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmCampaign,
      DataverseFieldName: 'mam_utmcampaign',
    });
  }
  if (utmParams.utmTerm) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmTerm,
      DataverseFieldName: 'mam_utmterm',
    });
  }
  if (utmParams.utmContent) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmContent,
      DataverseFieldName: 'mam_utmcontent',
    });
  }

  if (isGenericForm) {
    mappings.push({
      FormFieldName: sitecoreFormFields.dd1,
      DataverseFieldName: 'mam_marketingcustomfield1',
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.dd2,
      DataverseFieldName: 'mam_marketingcustomfield2',
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.dd3,
      DataverseFieldName: 'mam_marketingcustomfield3',
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.homeTypes,
      DataverseFieldName: 'mam_hometype',
    });
  }

  if (priceRangesValue || newBudgetValue) {
    mappings.push({
      FormFieldName: priceRangesValue
        ? sitecoreFormFields.priceRanges
        : sitecoreFormFields.newBudget,
      DataverseFieldName: 'mam_newbudget',
    });
  }

  if (firstTimeHomeBuyerValue) {
    mappings.push({
      FormFieldName: sitecoreFormFields.firstTimeHomeBuyer,
      DataverseFieldName: 'mam_areyouafirsttimehomebuyer',
    });
  }

  if (bedroomCountValue) {
    mappings.push({
      FormFieldName: sitecoreFormFields.bedroomCount,
      DataverseFieldName: 'mam_bedroomcount',
    });
  }

  if (desiredMoveDateValue) {
    mappings.push({
      FormFieldName: sitecoreFormFields.desiredMoveDate,
      DataverseFieldName: 'mam_desiredmovedate',
    });
  }

  mappings.push(
    ...buildExtraMappings({
      sitecoreForm,
      extraMappings,
      dynamicsFormFields,
      fieldRegistry,
      addNewLog,
    }),
  );

  return compactMappings(mappings);
};

export const logSitecoreFormDataSent = ({
  formElement,
  submitButton,
  contextId,
  addNewLog,
}) => {
  if (!formElement || !submitButton) return;

  const formData = new FormData(formElement, submitButton);
  let formDataString = '';

  for (const [key, value] of formData) {
    const field = document.getElementsByName(key)[0];
    const fieldLabel =
      field && field.labels && field.labels[0] && field.labels[0].textContent;
    if (fieldLabel && value) {
      formDataString += `${fieldLabel}: ${value}  |  `;
    }
  }

  addNewLog(
    100,
    '[Dynamics Forms API] Form data sended to Sitecore. ContextID: ' +
      contextId +
      '  |  ' +
      formDataString,
  );
};

export const submitDynaMXForm = ({
  sitecoreForm,
  mappings,
  dynaMXFormID,
  dynaMXFormAPIURL,
  sitecoreFormData,
  logList,
  addNewLog,
  saveLogs,
}) => {
  if (typeof window === 'undefined' || !window.d365mktformcapture) return;

  const serializedForm = window.d365mktformcapture.serializeForm(
    sitecoreForm,
    mappings,
  );
  const payload = serializedForm.SerializedForm.build();
  const captureConfig = {
    FormId: dynaMXFormID,
    FormApiUrl: dynaMXFormAPIURL,
  };

  addNewLog(
    100,
    '[Dynamics Forms API] API payload' + (payload.data || payload),
  );
  addNewLog(100, '[Dynamics Forms API] API called');

  Promise.resolve()
    .then(() => window.d365mktformcapture.submitForm(captureConfig, payload))
    .then(() => {
      addNewLog(100, '[Dynamics Forms API] API called successfully');
      saveLogs(
        logList,
        getPayloadVerifiedSitecoreFormData(sitecoreFormData, payload),
      );
    })
    .catch((e) => {
      console.log('d365mktformcapture.submitForm error', e);
      addNewLog(300, '[Dynamics Forms API] Something went wrong. ' + e);
      saveLogs(
        logList,
        getPayloadVerifiedSitecoreFormData(sitecoreFormData, payload),
      );
    });
};
